// Shared bits for the Payments and QR Payments screens.
import { useCallback, useEffect, useState } from "react";
import { aiAuthFetch } from "./aiAuthFetch";

export const CAMPUS_API_BASE = "https://maviinci-ai-bluepearl-e9750.web.app/api";

export const fmtINR = (v) => Number(v || 0).toLocaleString("en-IN", { maximumFractionDigits: 0, style: "currency", currency: "INR" });
export const fmtNum = (v) => Number(v || 0).toLocaleString("en-IN");

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/** "2026-09-19" → "19 Sep" */
export function fmtDay(day) {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(String(day || ""))) return day || "—";
    const [, m, d] = day.split("-");
    return `${Number(d)} ${MONTHS[Number(m) - 1] || ""}`.trim();
}

export function fmtRange(from, to) {
    if (!from && !to) return "";
    if (from && to) return from === to ? fmtDay(from) : `${fmtDay(from)} – ${fmtDay(to)}`;
    return fmtDay(from || to);
}

export function scopeLabel(drive) {
    const campus = drive?.campus || "";
    const range = fmtRange(drive?.from, drive?.to);
    if (!campus && !range) return "All campuses · all dates";
    return [campus || "All campuses", range].filter(Boolean).join(" · ");
}

/** Backend offloads big photos to "media:<id>" pointers. */
export const mediaUrl = (v) => {
    if (typeof v !== "string") return "";
    if (v.startsWith("media:")) return `${CAMPUS_API_BASE}/booking-media/${v.slice(6)}`;
    return v.startsWith("http") ? v : "";
};

// One QR fetch per session, shared by both tabs.
let qrCache = null;
let qrInFlight = null;
const qrWatchers = new Set();

function pushQr(state) {
    qrCache = state;
    qrWatchers.forEach((fn) => fn(state));
}

function loadQr(force) {
    if (!force && qrCache && qrCache.state === "ready") return Promise.resolve(qrCache);
    if (qrInFlight) return qrInFlight;
    pushQr({ state: "loading", rows: qrCache?.rows || [] });
    qrInFlight = aiAuthFetch(`${CAMPUS_API_BASE}/qr-payments`)
        .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
        .then((d) => {
            const next = { state: "ready", rows: Array.isArray(d.payments) ? d.payments : [], at: Date.now() };
            pushQr(next);
            return next;
        })
        .catch(() => {
            const next = { state: "error", rows: [] };
            pushQr(next);
            return next;
        })
        .finally(() => { qrInFlight = null; });
    return qrInFlight;
}

/** QR payments with a manual refresh — the log grows while a stall is live. */
export function useQrPayments() {
    const [snap, setSnap] = useState(() => qrCache || { state: "loading", rows: [] });

    useEffect(() => {
        qrWatchers.add(setSnap);
        loadQr(false);
        return () => { qrWatchers.delete(setSnap); };
    }, []);

    const refresh = useCallback(() => loadQr(true), []);
    return { rows: snap.rows, state: snap.state, at: snap.at, refresh };
}

export function Stat({ label, value, sub, tone }) {
    return (
        <div className={`campus-stat ${tone ? `campus-stat--${tone}` : ""}`}>
            <span className="campus-stat-label">{label}</span>
            <span className="campus-stat-value">{value}</span>
            {sub && <span className="campus-stat-sub">{sub}</span>}
        </div>
    );
}

export function Chips({ options, value, onChange }) {
    return (
        <div className="campus-hisab-views">
            {options.map((o) => (
                <button
                    type="button"
                    key={o.id}
                    className={`campus-drive-chip ${value === o.id ? "is-on" : ""}`}
                    onClick={() => onChange(o.id)}
                >
                    {o.label}
                    {o.count != null && <em>{fmtNum(o.count)}</em>}
                </button>
            ))}
        </div>
    );
}
